import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { getWorkerIntake, saveWorkerIntake } from "@/utils/worker-intake.functions";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { IntakeStepMyData } from "@/components/worker-intake/IntakeStepMyData";
import { IntakeStepPayment } from "@/components/worker-intake/IntakeStepPayment";
import { IntakeStepEmergency } from "@/components/worker-intake/IntakeStepEmergency";
import { IntakeStepW9 } from "@/components/worker-intake/IntakeStepW9";

export const Route = createFileRoute("/worker-intake/$token")({
  component: WorkerIntakePage,
  head: () => ({
    meta: [{ title: "Worker intake" }],
  }),
});

type Intake = Awaited<ReturnType<typeof getWorkerIntake>>;
type Section = "myData" | "payment" | "emergency" | "w9";

function WorkerIntakePage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const getIntake = useServerFn(getWorkerIntake);
  const save = useServerFn(saveWorkerIntake);

  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState<Intake | null>(null);
  const [error, setError] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await getIntake({ data: { token } });
        if (cancelled) return;
        setIntake(result);
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, getIntake]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !intake || !intake.ok) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
        <Card className="w-full max-w-md text-center">
          <CardHeader>
            <div className="mx-auto mb-2">
              <AlertCircle className="h-10 w-10 text-destructive" />
            </div>
            <CardTitle>{t("pages.workerIntake.invalid.title")}</CardTitle>
            <CardDescription>{t("pages.workerIntake.invalid.desc")}</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const steps: Section[] = [
    "myData",
    "payment",
    "emergency",
    ...(intake.include_tax ? (["w9"] as Section[]) : []),
  ];
  const total = steps.length;
  const isReview = stepIndex >= total;
  const company = intake.company;

  const back = () => setStepIndex((i) => Math.max(0, i - 1));

  const handleSubmit =
    (section: Section) => async (payload: Record<string, unknown>, mode: "next" | "exit") => {
      try {
        await save({ data: { token, section, payload } });
      } catch {
        toast.error(t("pages.workerIntake.errors.saveGeneric"));
        return;
      }
      if (mode === "exit") {
        toast.success(t("pages.workerIntake.savedExit"));
      } else {
        setStepIndex((i) => i + 1);
      }
    };

  const progressPct = isReview ? 100 : Math.round((stepIndex / total) * 100);
  const stepNumber = Math.min(stepIndex + 1, total);

  const renderStep = () => {
    const section = steps[stepIndex];
    const onBack = stepIndex > 0 ? back : undefined;
    if (section === "myData") {
      return (
        <IntakeStepMyData
          token={token}
          defaults={intake.myData}
          onSubmit={handleSubmit("myData")}
          onBack={onBack}
        />
      );
    }
    if (section === "payment") {
      return (
        <IntakeStepPayment
          defaults={intake.payment}
          onSubmit={handleSubmit("payment")}
          onBack={onBack}
        />
      );
    }
    if (section === "emergency") {
      return (
        <IntakeStepEmergency
          defaults={intake.emergency}
          onSubmit={handleSubmit("emergency")}
          onBack={onBack}
        />
      );
    }
    return (
      <IntakeStepW9
        token={token}
        defaults={intake.w9}
        personalAddress={intake.myData}
        onSubmit={handleSubmit("w9")}
        onBack={onBack}
      />
    );
  };

  return (
    <div className="min-h-screen bg-muted/30 px-4 py-8">
      <div className="mx-auto max-w-xl">
        <Card>
          <CardHeader className="text-center">
            {company?.logo_url ? (
              <img
                src={company.logo_url}
                alt={company?.name ?? ""}
                className="mx-auto mb-3 h-12 w-auto"
              />
            ) : null}
            <CardTitle>{company?.name ?? t("pages.workerIntake.title")}</CardTitle>
            {!isReview ? (
              <>
                <CardDescription>
                  {t("pages.workerIntake.stepCounter", { current: stepNumber, total })}
                </CardDescription>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted">
                  <div
                    className="h-full rounded-full bg-primary transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </>
            ) : null}
          </CardHeader>
          <CardContent>
            {isReview ? (
              <div className="flex flex-col items-center gap-3 py-6 text-center">
                <CheckCircle2 className="h-12 w-12 text-primary" />
                <h2 className="text-lg font-semibold">{t("pages.workerIntake.done.title")}</h2>
                <p className="text-sm text-muted-foreground">{t("pages.workerIntake.done.desc")}</p>
                <Button variant="outline" onClick={() => setStepIndex(0)}>
                  {t("pages.workerIntake.done.review")}
                </Button>
              </div>
            ) : (
              renderStep()
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
